import { supabase } from "@/integrations/supabase/client";

interface SavedGanttItem {
  id: string;
  lead_id: string;
  user_id: string;
  group_id: string | null;
  title: string;
  notes: string | null;
  start_position: number;
  width: number;
  row_index: number;
  color: string;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  time_duration: number;
  is_all_day: boolean;
  google_event_id: string | null;
}

export interface DeletedLeadData {
  ganttItems: SavedGanttItem[];
}

/**
 * Delete a lead and clean up its Google Calendar events for the current user
 * and all group members (if it's a group lead).
 * Returns the deleted gantt items so they can be restored on undo.
 */
export async function deleteLeadWithCalendarCleanup(
  leadId: string,
  options?: {
    groupId?: string | null;
    workspaceName?: string;
  }
): Promise<DeletedLeadData> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  // 1. Fetch gantt items for this lead before deletion
  const { data: ganttItems } = await supabase
    .from("lead_gantt_items")
    .select("*")
    .eq("lead_id", leadId);

  const savedItems: SavedGanttItem[] = (ganttItems || []).map((item) => ({
    id: item.id,
    lead_id: item.lead_id,
    user_id: item.user_id,
    group_id: item.group_id,
    title: item.title,
    notes: item.notes,
    start_position: item.start_position,
    width: item.width,
    row_index: item.row_index,
    color: item.color,
    start_date: item.start_date,
    end_date: item.end_date,
    start_time: item.start_time,
    time_duration: item.time_duration,
    is_all_day: item.is_all_day,
    google_event_id: item.google_event_id,
  }));

  // 2. Delete calendar events for each gantt item
  if (ganttItems && ganttItems.length > 0) {
    const groupId = options?.groupId || ganttItems[0]?.group_id || null;
    const isGroup = !!groupId;

    for (const item of ganttItems) {
      try {
        if (isGroup) {
          await supabase.functions.invoke("google-calendar-sync/sync-group", {
            method: "POST",
            headers: { Authorization: `Bearer ${session.access_token}` },
            body: {
              item: { id: item.id, google_event_id: item.google_event_id },
              leadName: "",
              leadId,
              groupId,
              workspaceName: options?.workspaceName || "Group",
              action: "delete",
            },
          });
        } else {
          await supabase.functions.invoke("google-calendar-sync/delete-item", {
            method: "POST",
            headers: { Authorization: `Bearer ${session.access_token}` },
            body: {
              googleEventId: item.google_event_id,
              ganttItemId: item.id,
              workspaceType: "personal",
              groupId: null,
              workspaceName: "Personal",
            },
          });
        }
      } catch (err) {
        console.error("Calendar cleanup error for gantt item:", item.id, err);
      }
    }

    // 3. Delete gantt items from DB
    await supabase
      .from("lead_gantt_items")
      .delete()
      .eq("lead_id", leadId);
  }

  // 4. Delete the lead
  const { error } = await supabase
    .from("leads")
    .delete()
    .eq("id", leadId);

  if (error) throw error;

  return { ganttItems: savedItems };
}

/**
 * Restore gantt items and re-sync them to Google Calendar.
 */
export async function restoreGanttItemsWithCalendarSync(
  ganttItems: SavedGanttItem[],
  leadName: string,
  options?: {
    groupId?: string | null;
    workspaceName?: string;
  }
): Promise<void> {
  if (!ganttItems || ganttItems.length === 0) return;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  // 1. Re-insert gantt items (without google_event_id since old events were deleted)
  for (const item of ganttItems) {
    const { error } = await supabase
      .from("lead_gantt_items")
      .insert({
        id: item.id,
        lead_id: item.lead_id,
        user_id: item.user_id,
        group_id: item.group_id,
        title: item.title,
        notes: item.notes,
        start_position: item.start_position,
        width: item.width,
        row_index: item.row_index,
        color: item.color,
        start_date: item.start_date,
        end_date: item.end_date,
        start_time: item.start_time,
        time_duration: item.time_duration,
        is_all_day: item.is_all_day,
        // Don't restore google_event_id — old events were deleted
      });

    if (error) {
      console.error("Error restoring gantt item:", item.id, error);
      continue;
    }

    // 2. Re-sync to Google Calendar
    const groupId = options?.groupId || item.group_id || null;
    const isGroup = !!groupId;

    try {
      if (isGroup) {
        await supabase.functions.invoke("google-calendar-sync/sync-group", {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            item: { ...item, google_event_id: null },
            leadName,
            leadId: item.lead_id,
            startDate: item.start_date,
            endDate: item.end_date,
            groupId,
            workspaceName: options?.workspaceName || "Group",
            action: "upsert",
          },
        });
      } else {
        await supabase.functions.invoke("google-calendar-sync/sync-item", {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            items: [{
              item: { ...item, google_event_id: null },
              leadName,
              leadId: item.lead_id,
              startDate: item.start_date,
              endDate: item.end_date,
              workspaceType: "personal",
              groupId: null,
              workspaceName: "Personal",
            }],
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });
      }
    } catch (err) {
      console.error("Calendar re-sync error for gantt item:", item.id, err);
    }
  }
}

/**
 * Bulk delete leads with calendar cleanup.
 */
export async function deleteLeadsWithCalendarCleanup(
  leadIds: string[],
  options?: {
    groupId?: string | null;
    workspaceName?: string;
  }
): Promise<void> {
  const BATCH_SIZE = 5;
  for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
    const batch = leadIds.slice(i, i + BATCH_SIZE);
    await Promise.allSettled(
      batch.map((id) => deleteLeadWithCalendarCleanup(id, options))
    );
  }
}
